/**
 * Pure budget-vs-actual comparison helpers.
 *
 * With bank imports feeding the transactions list, the app has two kinds of
 * expense rows and this module draws the line between them:
 *
 *   - "Budgeted" is the plan: a category's monthly limit when one is set,
 *     otherwise the monthly-normalized planned/recurring templates in that
 *     category (rent, subscriptions, …).
 *   - "Actual" is what really happened: every non-template expense row in the
 *     month — bank-imported transactions and one-off manual entries alike.
 *     Recurring templates are planning artifacts; the occurrences they
 *     generate carry the actual spend.
 *
 * Kept dependency-free (like expenseFilters) so the math is unit-testable
 * without spinning up Vue components or store mocks.
 */

import type { FirestoreCategory, FirestoreExpense } from "../types/firestore"
import { BANK_SOURCE } from "./expenseFilters"
import { toMonthly } from "./monthlyIncome"

/** Row key for actual spend whose category doesn't resolve to any category. */
export const UNCATEGORIZED_KEY = "__uncategorized__"

/**
 * Normalize a recurring amount to its monthly equivalent. Delegates to the
 * shared conversion table (monthlyIncome.ts is the declared single source of
 * truth for the 4.33/2.17 factors) — a local copy here had already started
 * to drift on unknown frequencies. Expense templates never carry "one-time",
 * so the delegation is behavior-preserving.
 */
export function monthlyPlannedAmount(amount: number, frequency: string | undefined): number {
  return toMonthly(amount, frequency)
}

/** "YYYY-MM" key for a date. */
export function monthKeyOf(date: Date): string {
  const m = date.getMonth() + 1
  return `${date.getFullYear()}-${m < 10 ? `0${m}` : m}`
}

/** Shift a "YYYY-MM" key by a number of months (delta may be negative). */
export function shiftMonthKey(key: string, delta: number): string {
  const [year, month] = key.split("-").map(Number)
  return monthKeyOf(new Date(year, month - 1 + delta, 1))
}

/**
 * True for planning-template rows: they describe intended spending, so they
 * count toward the budget, never toward actuals.
 */
export function isPlanningTemplate(expense: Pick<FirestoreExpense, "isRecurring">): boolean {
  return expense.isRecurring === true
}

/**
 * True for a bank-imported row that restates an expense already recorded by
 * hand. Bank sync only dedups against earlier imports, so the bank's copy of a
 * fixed cost the user already tracks arrives as a second row and every total
 * counted the money twice.
 *
 * Such rows stay visible in the transaction list — that is where they are
 * reviewed — but must not be summed. `duplicateOf` is cleared when the user
 * says the two really are separate payments, after which it counts normally.
 */
export function isSuspectedDuplicate(expense: Pick<FirestoreExpense, "duplicateOf">): boolean {
  return Boolean(expense.duplicateOf)
}

/**
 * True for money moved between the user's own accounts. The bank reports each
 * leg and the importer maps every debit to an expense, so on a multi-account
 * setup a fifth of "spending" can be balance regulation that never left the
 * household — measured at 11 882 kr of 32 033 kr in one real month.
 *
 * Still listed: it is the truthful record that the money moved.
 */
export function isInternalTransfer(expense: Pick<FirestoreExpense, "isTransfer">): boolean {
  return expense.isTransfer === true
}

/**
 * The filter every spending total wants: what actually left the household.
 * Plans, duplicate restatements and internal transfers are none of them.
 */
export function countsTowardSpending(
  expense: Pick<FirestoreExpense, "isRecurring" | "duplicateOf" | "isTransfer">,
): boolean {
  return !isPlanningTemplate(expense) && !isSuspectedDuplicate(expense) && !isInternalTransfer(expense)
}

export interface BudgetVsActualRow {
  /** Category id, or UNCATEGORIZED_KEY for unresolved actuals. */
  key: string
  /** Category display name; empty for the uncategorized bucket. */
  name: string
  color: string | null
  /** The category's monthly limit (0 when unset). */
  budgetLimit: number
  /** Monthly-normalized planned/recurring templates in this category. */
  plannedRecurring: number
  /** Effective plan: the explicit limit when set, else the planned total. */
  budgeted: number
  /** Non-template spend in the month (bank + manual). */
  actual: number
  /** Portion of `actual` imported from a linked bank. */
  actualBank: number
  /** Portion of `actual` entered by hand (incl. generated occurrences). */
  actualManual: number
  /** budgeted - actual; positive = money left, negative = over plan. */
  diff: number
}

export interface BudgetVsActualTotals {
  budgeted: number
  actual: number
  actualBank: number
  actualManual: number
  diff: number
}

export interface BudgetVsActualResult {
  rows: BudgetVsActualRow[]
  totals: BudgetVsActualTotals
}

// A template contributes to a month's plan while its schedule covers that
// month: started on/before the month's end, not ended before the month, and
// not paused. Date strings are "YYYY-MM-DD", so YYYY-MM prefix compares work.
function templateCoversMonth(template: FirestoreExpense, month: string): boolean {
  if (template.isActive === false) return false
  if (template.date && template.date.slice(0, 7) > month) return false
  if (template.endDate && template.endDate.slice(0, 7) < month) return false
  return true
}

/**
 * Compare the plan against reality for one calendar month ("YYYY-MM").
 *
 * Returns one row per category that has either a plan or actual spend, plus
 * an uncategorized bucket when actual rows don't resolve to any category.
 * Rows are sorted by the larger of plan/actual, descending, so the biggest
 * budget lines lead.
 */
export function computeBudgetVsActual(
  expenses: FirestoreExpense[],
  categories: FirestoreCategory[],
  month: string,
): BudgetVsActualResult {
  // Expenses reference their category by id on new rows and by name on legacy
  // ones — resolve both to the same bucket.
  const categoryByRef = new Map<string, FirestoreCategory>()
  for (const c of categories) {
    categoryByRef.set(c.id, c)
    categoryByRef.set(c.name, c)
  }

  const planned = new Map<string, number>()
  const actual = new Map<string, number>()
  const actualBank = new Map<string, number>()
  const add = (map: Map<string, number>, key: string, amount: number) => {
    map.set(key, (map.get(key) || 0) + amount)
  }

  for (const e of expenses) {
    if (e.type !== "expense") continue
    const category = categoryByRef.get(e.category)
    if (isPlanningTemplate(e)) {
      if (!category || !templateCoversMonth(e, month)) continue
      add(planned, category.id, monthlyPlannedAmount(e.amount, e.frequency))
    } else {
      // A bank row restating an expense already recorded by hand is the same
      // money seen twice; counting it inflated both the actual and the
      // over-budget percentage.
      if (isSuspectedDuplicate(e)) continue
      // Moving money between your own accounts is not spending against a
      // category budget, however the bank reports it.
      if (isInternalTransfer(e)) continue
      if (!e.date.startsWith(month)) continue
      const key = category?.id ?? UNCATEGORIZED_KEY
      add(actual, key, e.amount)
      if (e.source === BANK_SOURCE) add(actualBank, key, e.amount)
    }
  }

  const rows: BudgetVsActualRow[] = []
  for (const c of categories) {
    const budgetLimit = c.budget && c.budget > 0 ? c.budget : 0
    const plannedRecurring = planned.get(c.id) || 0
    const budgeted = budgetLimit > 0 ? budgetLimit : plannedRecurring
    const actualTotal = actual.get(c.id) || 0
    if (budgeted <= 0 && actualTotal <= 0) continue
    const bank = actualBank.get(c.id) || 0
    rows.push({
      key: c.id,
      name: c.name,
      color: c.color || null,
      budgetLimit,
      plannedRecurring,
      budgeted,
      actual: actualTotal,
      actualBank: bank,
      actualManual: actualTotal - bank,
      diff: budgeted - actualTotal,
    })
  }

  const uncategorized = actual.get(UNCATEGORIZED_KEY) || 0
  if (uncategorized > 0) {
    const bank = actualBank.get(UNCATEGORIZED_KEY) || 0
    rows.push({
      key: UNCATEGORIZED_KEY,
      name: "",
      color: null,
      budgetLimit: 0,
      plannedRecurring: 0,
      budgeted: 0,
      actual: uncategorized,
      actualBank: bank,
      actualManual: uncategorized - bank,
      diff: -uncategorized,
    })
  }

  rows.sort((a, b) => Math.max(b.budgeted, b.actual) - Math.max(a.budgeted, a.actual))

  const totals = rows.reduce(
    (sum, row) => {
      sum.budgeted += row.budgeted
      sum.actual += row.actual
      sum.actualBank += row.actualBank
      sum.actualManual += row.actualManual
      return sum
    },
    { budgeted: 0, actual: 0, actualBank: 0, actualManual: 0, diff: 0 },
  )
  totals.diff = totals.budgeted - totals.actual

  return { rows, totals }
}
